import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  Popover,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  Chip,
} from "@mui/material";
import MainCard from "../../../components/MainCard";
import { useMemo, useState } from "react";
import EnhancedTableHead from "../../../components/EnhancedTableHead";
import { useTheme } from "@emotion/react";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import LightBox from "../../../components/LightBox";
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PlaywrightRetrySubRows from "./PlaywrightRetryAttempts";
import {
  formatFailureTrace,
  formatLogsForDisplay,
  isStepFailed,
  isStepPassed,
} from "../../../utils/testCaseFailureUtils";

const LogsBlock = ({ logs, theme }) => {
  const text = formatLogsForDisplay(logs);
  if (!text) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Chip
        label="Logs"
        size="small"
        sx={{
          mb: 0.75,
          height: 22,
          fontWeight: 700,
          bgcolor: "#eff6ff",
          color: "#1d4ed8",
        }}
      />
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: theme.palette.mode === "dark" ? "#0f172a" : "#1e293b",
          color: "#93c5fd",
          fontFamily: "'Roboto Mono', monospace",
          fontSize: "0.75rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxHeight: 240,
          overflow: "auto",
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

const getHeadCells = (supportsRetries) => {
  const base = [
    {
      id: "testCaseId",
      label: "ID",
      align: "left",
      sortable: false,
      width: supportsRetries ? "18%" : "20%",
      filterable: true,
    },
    {
      id: "summary",
      label: "Summary",
      align: "left",
      sortable: false,
      width: supportsRetries ? "32%" : "50%",
      filterable: true,
    },
    {
      id: "result",
      label: "Result",
      align: "left",
      sortable: false,
      width: "8%",
      filterable: true,
    },
    {
      id: "executionTime",
      label: "Execution Time",
      align: "left",
      sortable: true,
      width: "8%",
      filterable: false,
    },
  ];

  if (supportsRetries) {
    base.push({
      id: "retryCount",
      label: "Retry",
      align: "center",
      sortable: false,
      width: "8%",
      filterable: false,
    });
  }

  base.push({
    id: "action",
    label: "Action",
    align: "center",
    sortable: false,
    width: supportsRetries ? "8%" : "10%",
  });

  return base;
};

const TestCasesTable = ({ testCases, supportsRetries = false }) => {
  const headCells = useMemo(() => getHeadCells(supportsRetries), [supportsRetries]);
  const columnCount = headCells.length;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("executionTime");

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState("");
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [modalVideoUrl, setModalVideoUrl] = useState("");

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [filters, setFilters] = useState({
    testCaseId: "",
    summary: "",
    result: "All",
  });

  const [tempFilters, setTempFilters] = useState({
    testCaseId: "",
    summary: "",
    result: "All",
  });

  const closeLightBox = () => {
    setIsImageModalOpen(false);
    setModalImageUrl("");
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setModalVideoUrl("");
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleFilterClick = (event, columnId) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(columnId);
    setTempFilters((prev) => ({
      ...prev,
      [columnId]: filters[columnId]
    }));
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleTempFilterChange = (columnId, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const handleApplyFilter = () => {
    setFilters((prev) => ({
      ...prev,
      [activeFilterColumn]: tempFilters[activeFilterColumn]
    }));
    setPage(0);
    handleFilterClose();
  };

  const handleClearColumnFilter = () => {
    const defaultValue = activeFilterColumn === 'result' ? 'All' : '';
    setFilters((prev) => ({
      ...prev,
      [activeFilterColumn]: defaultValue
    }));
    setTempFilters((prev) => ({
      ...prev,
      [activeFilterColumn]: defaultValue
    }));
    setPage(0);
    handleFilterClose();
  };

  const formatTimeSeconds = (timeInSeconds) => {
    if (timeInSeconds === undefined || timeInSeconds === null || isNaN(timeInSeconds)) return 0;
    return parseFloat(timeInSeconds);
  };

  const filteredAndSortedTestCases = useMemo(() => {
    if (!testCases) return [];

    let resultList = testCases.filter((tc) => {
      // ID Filter
      if (filters.testCaseId && !tc.testCaseId?.toLowerCase().includes(filters.testCaseId.toLowerCase())) {
        return false;
      }
      // Summary Filter
      if (filters.summary && !tc.testCaseSummary?.toLowerCase().includes(filters.summary.toLowerCase())) {
        return false;
      }
      if (filters.result !== "All" && tc.result !== filters.result) {
        return false;
      }
      return true;
    });

    // Sorting
    resultList.sort((a, b) => {
      let valA = a[orderBy];
      let valB = b[orderBy];

      if (orderBy === 'executionTime') {
        valA = formatTimeSeconds(valA);
        valB = formatTimeSeconds(valB);
      }

      if (valA < valB) {
        return order === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return resultList;
  }, [filters, testCases, order, orderBy]);

  const visibleRows = useMemo(
    () =>
      filteredAndSortedTestCases?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, filteredAndSortedTestCases]
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredAndSortedTestCases?.length) : 0;

  const resetFilters = () => {
    setFilters({
      testCaseId: "",
      summary: "",
      result: "All",
    });
    handleFilterClose();
  };

  return (
    <MainCard>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {(filters.testCaseId || filters.summary || filters.result !== "All") && (
          <Button size="small" variant="text" color="error" onClick={resetFilters}>
            Clear All Filters
          </Button>
        )}
      </Box>
      {filteredAndSortedTestCases?.length > 0 ? (
        <Box sx={{ width: "100%" }}>
          <Paper sx={{ width: "100%", mb: 2 }}>
            <TableContainer>
              <Table
                sx={{ minWidth: 750, tableLayout: "fixed" }}
                aria-labelledby="tableTitle"
                size="small">
                <EnhancedTableHead
                  sortable={true}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headCells={headCells}
                  onFilterClick={handleFilterClick}
                />
                <TableBody>
                  {visibleRows.map((testCase, index) => {
                    return (
                      <CollapsibleRow
                        testCase={testCase}
                        key={index}
                        theme={theme}
                        supportsRetries={supportsRetries}
                        columnCount={columnCount}
                        setIsImageModalOpen={setIsImageModalOpen}
                        setModalImageUrl={setModalImageUrl}
                        setIsVideoModalOpen={setIsVideoModalOpen}
                        setModalVideoUrl={setModalVideoUrl}
                      />
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: 53 * emptyRows,
                      }}>
                      <TableCell colSpan={columnCount} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 15, 30]}
              component="div"
              count={filteredAndSortedTestCases?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
            {isImageModalOpen && (
              <LightBox
                image={modalImageUrl}
                title="Failure Screenshot"
                allowRotate={false}
                onClose={closeLightBox}
                doubleClickZoom={0}
              />
            )}
            <Dialog
              open={isVideoModalOpen}
              onClose={closeVideoModal}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle>Failure Video</DialogTitle>
              <DialogContent>
                {modalVideoUrl && (
                  <video
                    src={modalVideoUrl}
                    controls
                    autoPlay
                    style={{ width: "100%", maxHeight: "70vh" }}
                  />
                )}
              </DialogContent>
            </Dialog>
            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: { p: 2, width: 250 }
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ mb: 1 }}>Filter {headCells.find(h => h.id === activeFilterColumn)?.label}</Typography>
                {activeFilterColumn === "testCaseId" || activeFilterColumn === "summary" ? (
                  <TextField
                    autoFocus
                    size="small"
                    fullWidth
                    label={`Search ${headCells.find(h => h.id === activeFilterColumn)?.label}`}
                    value={tempFilters[activeFilterColumn]}
                    onChange={(e) => handleTempFilterChange(activeFilterColumn, e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : activeFilterColumn === "result" ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Result</InputLabel>
                    <Select
                      value={tempFilters.result}
                      label="Result"
                      onChange={(e) => handleTempFilterChange("result", e.target.value)}
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Pass">Pass</MenuItem>
                      <MenuItem value="Fail">Fail</MenuItem>
                      <MenuItem value="Skip">Skip</MenuItem>
                    </Select>
                  </FormControl>
                ) : null}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button size="small" color="inherit" onClick={handleClearColumnFilter}>
                    Clear
                  </Button>
                  <Button size="small" variant="contained" onClick={handleApplyFilter} sx={{ ml: 1 }}>
                    Apply
                  </Button>
                </Box>
              </Stack>
            </Popover>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" align="center">
            No Records found matching current filters.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="outlined" onClick={resetFilters}>
              Reset All Filters
            </Button>
          </Box>
        </Box>
      )}
    </MainCard>
  );
};

const CollapsibleRow = ({
  testCase,
  theme,
  supportsRetries,
  columnCount,
  setModalImageUrl,
  setIsImageModalOpen,
  setModalVideoUrl,
  setIsVideoModalOpen,
}) => {
  const retryCount = testCase.retryCount ?? 0;
  const canExpandRetries =
    supportsRetries &&
    retryCount > 0 &&
    Array.isArray(testCase.retryAttempts) &&
    testCase.retryAttempts.length > 0;

  const openImage = (event, url) => {
    event.stopPropagation();
    setModalImageUrl(url || testCase.screenshot);
    setIsImageModalOpen(true);
  };

  const openVideo = (event, url) => {
    event.stopPropagation();
    setModalVideoUrl(url || testCase.video);
    setIsVideoModalOpen(true);
  };
  const [open, setOpen] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);

  const stepsHaveLogs =
    testCase.steps?.some((step) => Boolean(step.logs)) ?? false;
  const showAggregateLogs =
    Boolean(testCase.logs) &&
    (!testCase.steps?.length || !stepsHaveLogs);
  const hasCollapsibleDetails =
    Boolean(testCase.failureTrace) ||
    showAggregateLogs ||
    stepsHaveLogs ||
    (testCase.steps && testCase.steps.length > 0);

  const handleRetryClick = (event) => {
    event.stopPropagation();
    if (canExpandRetries) {
      setRetryOpen((prev) => !prev);
    }
  };

  const truncateString = (str, num) => {
    if (!str) return "";
    if (str.length <= num) {
      return str;
    }
    return str.slice(0, num) + "...";
  };

  const formatTime = (timeInSeconds) => {
    if (timeInSeconds === undefined || timeInSeconds === null || isNaN(timeInSeconds)) return "00:00:00";
    const totalSeconds = Math.floor(parseFloat(timeInSeconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds]
      .map((v) => (v < 10 ? "0" + v : v))
      .join(":");
  };

  return (
    <>
      <TableRow
        hover
        onClick={() => {
          hasCollapsibleDetails && setOpen(!open);
        }}
        tabIndex={-1}
        sx={{ cursor: hasCollapsibleDetails ? "pointer" : "default" }}>
        <TableCell sx={{ wordBreak: "break-all" }}>
          <Tooltip title={testCase.testCaseId}>
            <span>{truncateString(testCase.testCaseId, 70)}</span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ wordBreak: "break-all" }}>
          <Tooltip title={testCase.testCaseSummary}>
            <span>{truncateString(testCase.testCaseSummary, 100)}</span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Typography
            sx={{
              color:
                testCase.result === "Pass"
                  ? theme.palette.success.main
                  : testCase.result === "Fail"
                    ? theme.palette.error.main
                    : theme.palette.orange.main,
              fontWeight: "bold",
            }}>
            {testCase.result}
          </Typography>
        </TableCell>
        <TableCell align="center">{formatTime(testCase.executionTime)}</TableCell>
        {supportsRetries && (
          <TableCell align="center" onClick={handleRetryClick}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent="center"
              sx={{
                cursor: canExpandRetries ? "pointer" : "default",
              }}
            >
              {canExpandRetries &&
                (retryOpen ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                ))}
              <Chip
                label={retryCount}
                size="small"
                sx={{
                  fontWeight: 800,
                  minWidth: 36,
                  bgcolor: retryCount > 0 ? "#fff7ed" : "#f1f5f9",
                  color: retryCount > 0 ? "#c2410c" : "#64748b",
                  border: retryCount > 0 ? "1px solid #fdba74" : "1px solid #e2e8f0",
                }}
              />
            </Stack>
          </TableCell>
        )}
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Tooltip title="Failure Screenshot (latest attempt)">
              <IconButton
                sx={{
                  visibility:
                    testCase.screenshot && testCase.screenshot.trim() !== ""
                      ? "visible"
                      : "hidden",
                }}
                size="large"
                color="primary"
                tabIndex={-1}
                onClick={(e) => openImage(e)}>
                <ImageIcon color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Failure Video (latest attempt)">
              <IconButton
                sx={{
                  visibility:
                    testCase.video && testCase.video.trim() !== ""
                      ? "visible"
                      : "hidden",
                }}
                size="large"
                color="secondary"
                tabIndex={-1}
                onClick={(e) => openVideo(e)}>
                <VideocamIcon color="secondary" />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      <PlaywrightRetrySubRows
        attempts={testCase.retryAttempts}
        theme={theme}
        retryOpen={canExpandRetries && retryOpen}
        showRetryColumn={supportsRetries}
        columnCount={columnCount}
        onOpenImage={openImage}
        onOpenVideo={openVideo}
      />
      {hasCollapsibleDetails && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  margin: 2,
                  padding: 2,
                  backgroundColor: testCase.result === 'Pass'
                    ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.05)' : 'rgba(76, 175, 80, 0.02)')
                    : (theme.palette.mode === 'dark' ? 'rgba(255, 0, 0, 0.05)' : '#fff5f5'),
                  border: `1px solid ${testCase.result === 'Pass' ? theme.palette.success.light : theme.palette.error.light}`,
                  borderRadius: '8px',
                  boxShadow: 'inset 0 0 10px rgba(0,0,0,0.02)'
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  component="div"
                  sx={{
                    color: testCase.result === 'Pass' ? theme.palette.success.main : theme.palette.error.main,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5
                  }}
                >
                  {testCase.steps && testCase.steps.length > 0
                    ? "Execution Steps"
                    : testCase.failureTrace
                      ? "Failure Cause"
                      : "Logs"}
                </Typography>

                {testCase.steps && testCase.steps.length > 0 ? (
                  <Stack spacing={1}>
                    {testCase.steps.map((step, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          padding: 1.25,
                          borderRadius: '6px',
                          mb: 1,
                          borderLeft: `4px solid ${isStepPassed(step.status) ? theme.palette.success.main :
                            isStepFailed(step.status) ? theme.palette.error.main :
                              theme.palette.text.disabled
                            }`,
                          backgroundColor: isStepPassed(step.status)
                            ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)')
                            : isStepFailed(step.status)
                              ? (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)')
                              : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0,0,0,0.02)'),
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isStepPassed(step.status)
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)')
                              : isStepFailed(step.status)
                                ? (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.08)')
                                : 'rgba(0,0,0,0.04)',
                            transform: 'translateX(2px)'
                          }
                        }}
                      >
                        <Typography sx={{
                          fontFamily: "'Roboto Mono', monospace",
                          fontSize: '0.85rem',
                          whiteSpace: 'pre-wrap',
                          color: isStepPassed(step.status) ? theme.palette.success.dark :
                            isStepFailed(step.status) ? theme.palette.error.dark :
                              theme.palette.text.secondary
                        }}>
                          {step.name}
                        </Typography>
                        {step.errorMessage && (
                          <Box
                            component="pre"
                            sx={{
                              margin: 1,
                              padding: 1,
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)',
                              borderRadius: '4px',
                              fontFamily: "'Roboto Mono', monospace",
                              fontSize: '0.75rem',
                              overflowX: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              color: theme.palette.error.main,
                              border: `1px dashed ${theme.palette.error.light}`
                            }}
                          >
                            {step.errorMessage}
                          </Box>
                        )}
                        {step.logs && <LogsBlock logs={step.logs} theme={theme} />}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      margin: 0,
                      padding: 1.5,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)',
                      borderRadius: '4px',
                      fontFamily: "'Roboto Mono', monospace",
                      fontSize: '0.8125rem',
                      overflowX: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      color: theme.palette.text.primary,
                      borderLeft: `4px solid ${theme.palette.error.main}`
                    }}
                  >
                    {formatFailureTrace(testCase.failureTrace)}
                  </Box>
                )}

                {showAggregateLogs && <LogsBlock logs={testCase.logs} theme={theme} />}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default TestCasesTable;
